import clientAxios from "../config";

/**
 * Crea un Agreement. Devuelve JSON con el agreement creado (status
 * "Pendiente de firmas"). El PDF ya no se descarga aquí — se obtiene
 * con getAgreementPdf(uuid) cuando se necesite preview o descarga.
 */
const makeNewAgreement = async (token, data) => clientAxios.post(`/api/agreement`, data, {
    headers: { Authorization: `Bearer ${token}` },
});

/**
 * Lista los contratos de una empresa (por uuid del user company).
 * Acepta filtros: status, date_from, date_to + paginación (page, per_page).
 */
const loadCompanyAgreements = async (token, uuid, params = {}) => clientAxios.get(`/api/agreement-company/${uuid}`, {
    headers: { Authorization: `Bearer ${token}` },
    params,
});

/**
 * Lista TODOS los contratos del sistema. Solo accesible para admin
 * (el back valida con la policy viewAny). Usado por el listado de
 * AgreementsPage cuando el usuario logueado es admin.
 * Acepta filtros: status, company, date_from, date_to + paginación
 * (page, per_page).
 */
const loadAllAgreements = async (token, params = {}) => clientAxios.get(`/api/agreement`, {
    headers: { Authorization: `Bearer ${token}` },
    params,
});

/**
 * Detalle completo de un agreement.
 */
const getAgreement = async (token, uuid) => clientAxios.get(`/api/agreement/${uuid}`, {
    headers: { Authorization: `Bearer ${token}` },
});

/**
 * Edita el contenido de un agreement antes de su aprobación.
 * Si el agreement tenía firmas, el back las invalida.
 */
const updateAgreementContent = async (token, uuid, data) => clientAxios.put(`/api/agreement/${uuid}`, data, {
    headers: { Authorization: `Bearer ${token}` },
});

/**
 * Cancela un agreement.
 */
const cancelAgreement = async (token, uuid) => clientAxios.get(`/api/agreement-cancel/${uuid}`, {
    headers: { Authorization: `Bearer ${token}` },
});

/**
 * Firma un agreement como empresa. signatureDataUrl debe ser el data
 * URL devuelto por SignaturePadModal (data:image/png;base64,...).
 */
const signAgreementAsCompany = async (token, uuid, signatureDataUrl) => clientAxios.post(
    `/api/agreement/${uuid}/sign-company`,
    { signature: signatureDataUrl },
    { headers: { Authorization: `Bearer ${token}` } }
);

/**
 * Firma un agreement como Fenabocol (admin). El back valida que sea
 * el admin que creó el contrato (o cualquier admin si lo creó la empresa).
 */
const signAgreementAsFenabocol = async (token, uuid, signatureDataUrl) => clientAxios.post(
    `/api/agreement/${uuid}/sign-fenabocol`,
    { signature: signatureDataUrl },
    { headers: { Authorization: `Bearer ${token}` } }
);

/**
 * Aprueba un agreement firmado por ambas partes y dispara la entrega
 * de códigos. Solo admin.
 */
const approveAgreement = async (token, uuid) => clientAxios.post(
    `/api/agreement/${uuid}/approve`,
    {},
    { headers: { Authorization: `Bearer ${token}` } }
);

/**
 * Inicia el cobro Wompi para un agreement firmado por ambas partes
 * cuya forma de pago sea Wompi. Solo lo puede invocar la empresa
 * dueña del contrato. Devuelve el payload firmado para abrir el
 * widget; al confirmarse el pago, el back aprueba el contrato y
 * entrega los códigos automáticamente.
 */
const checkoutAgreementWompi = async (token, uuid) => clientAxios.post(
    `/api/agreement/${uuid}/checkout-wompi`,
    {},
    { headers: { Authorization: `Bearer ${token}` } }
);

/**
 * Descarga el PDF del agreement como blob. Si está aprobado devuelve
 * el snapshot cacheado, sino genera dinámicamente con las firmas
 * presentes hasta el momento.
 */
const getAgreementPdf = async (token, uuid) => clientAxios.get(`/api/agreement/${uuid}/pdf`, {
    headers: { Authorization: `Bearer ${token}` },
    responseType: 'blob',
});

/**
 * Helper para descargar el blob como archivo .pdf en el navegador.
 */
const downloadAgreementPdf = async (token, uuid, filename) => {
    const response = await getAgreementPdf(token, uuid);
    const blob = new Blob([response.data], { type: 'application/pdf' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename ?? `contrato_${uuid}.pdf`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
};

export {
    makeNewAgreement,
    loadCompanyAgreements,
    loadAllAgreements,
    getAgreement,
    updateAgreementContent,
    cancelAgreement,
    signAgreementAsCompany,
    signAgreementAsFenabocol,
    approveAgreement,
    checkoutAgreementWompi,
    getAgreementPdf,
    downloadAgreementPdf,
};
